import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Clock, Calendar, X } from "lucide-react";

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export default function SubscriptionBanner() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user?.role !== "clinic_manager") return;
    (async () => {
      try { const r = await api.get("/billing/subscription"); setSub(r.data); }
      catch { /* ignore */ }
    })();
  }, [user]);

  if (user?.role !== "clinic_manager" || !sub || dismissed) return null;

  // Trial banner — show whenever clinic is still on trial
  if (sub.status === "trial") {
    const daysLeft = daysUntil(sub.trial_ends_at);
    if (daysLeft != null && daysLeft <= 0) {
      return (
        <BannerShell color="#09090b" textColor="#fff" onDismiss={() => setDismissed(true)} tid="banner-trial-ended">
          <strong>Free trial ended.</strong>
          <span className="opacity-90">Choose a plan to keep your dashboards and analytics active.</span>
          <Link to="/pricing" className="cta-gold" data-testid="banner-trial-cta">Choose a plan</Link>
        </BannerShell>
      );
    }
    return (
      <BannerShell color="var(--gold-light)" textColor="#7a5d10" onDismiss={() => setDismissed(true)} tid="banner-trial">
        <Clock className="w-4 h-4" />
        <strong>Trial ends in {daysLeft ?? "—"} day{daysLeft === 1 ? "" : "s"}.</strong>
        <span className="opacity-80 hidden sm:inline">Upgrade now to keep clinic analytics and sonographer invitations active.</span>
        <Link to="/pricing" className="cta-gold" data-testid="banner-trial-cta">Upgrade now</Link>
      </BannerShell>
    );
  }

  // Renewal banner — show within 30 days of subscription_ends_at
  if (sub.status === "active") {
    const daysLeft = daysUntil(sub.subscription_ends_at);
    if (daysLeft != null && daysLeft <= 30 && daysLeft > 0) {
      const renewDate = (sub.subscription_ends_at || "").slice(0, 10);
      return (
        <BannerShell color="#0f7d56" textColor="#fff" onDismiss={() => setDismissed(true)} tid="banner-renewal">
          <Calendar className="w-4 h-4" />
          <strong>Renewal in {daysLeft} day{daysLeft === 1 ? "" : "s"}.</strong>
          <span className="opacity-90 hidden sm:inline">
            Your <span className="capitalize">{sub.plan}</span> plan renews automatically on {renewDate}.
          </span>
          <Link to="/pricing" className="cta-on-dark" data-testid="banner-renewal-cta">Manage billing</Link>
        </BannerShell>
      );
    }
    if (daysLeft != null && daysLeft <= 0) {
      return (
        <BannerShell color="#b54708" textColor="#fff" onDismiss={() => setDismissed(true)} tid="banner-expired">
          <strong>Subscription expired.</strong>
          <span className="opacity-90">Renew to restore full clinic access.</span>
          <Link to="/pricing" className="cta-on-dark" data-testid="banner-renewal-cta">Renew now</Link>
        </BannerShell>
      );
    }
  }

  return null;
}

function BannerShell({ color, textColor, onDismiss, tid, children }) {
  return (
    <div className="-mx-4 sm:-mx-8 -mt-4 sm:-mt-8 mb-6" data-testid={tid}>
      <div className="px-4 sm:px-8 py-3 flex flex-wrap items-center gap-3 text-sm" style={{ background: color, color: textColor }}>
        {children}
        <button onClick={onDismiss} className="ml-auto opacity-70 hover:opacity-100" data-testid={`${tid}-dismiss`} aria-label="Dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
      <style>{`
        .cta-gold{ background: var(--gold); color: #09090b; padding: 6px 14px; border-radius: 6px; font-weight: 700; }
        .cta-gold:hover{ background: var(--gold-hover); color: #fff; }
        .cta-on-dark{ background: #fff; color: #09090b; padding: 6px 14px; border-radius: 6px; font-weight: 700; }
      `}</style>
    </div>
  );
}
